import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useCreateEntidade, type CreateEntidadeInput } from '@/lib/hooks/entidades';

interface FormValues {
  cnpj: string;
  razaoSocial: string;
  nomeFantasia: string;
  enderecoCompleto: string;
  municipioUf: string;
}

interface Props {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DEFAULTS: FormValues = {
  cnpj: '',
  razaoSocial: '',
  nomeFantasia: '',
  enderecoCompleto: '',
  municipioUf: '',
};

function fmtCnpj(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12)
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

export function EntidadeDrawer({ workspaceId, open, onOpenChange }: Props) {
  const createMut = useCreateEntidade(workspaceId);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: DEFAULTS });

  const cnpjValue = watch('cnpj');

  useEffect(() => {
    if (!open) reset(DEFAULTS);
  }, [open, reset]);

  function handleCnpjChange(e: React.ChangeEvent<HTMLInputElement>) {
    setValue('cnpj', fmtCnpj(e.target.value));
  }

  async function onSubmit(values: FormValues) {
    const payload: CreateEntidadeInput = {
      cnpj: values.cnpj,
      razaoSocial: values.razaoSocial,
    };
    if (values.nomeFantasia) payload.nomeFantasia = values.nomeFantasia;
    if (values.enderecoCompleto) payload.enderecoCompleto = values.enderecoCompleto;
    if (values.municipioUf) payload.municipioUf = values.municipioUf;

    try {
      await createMut.mutateAsync(payload);
      toast.success('Entidade cadastrada.');
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao cadastrar entidade.');
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <SheetHeader
            eyebrow="Nova entidade"
            title="Cadastrar entidade"
            description="Organização parceira vinculada ao convênio."
          />
          <SheetBody className="space-y-5">
            <div>
              <Label htmlFor="cnpj" className="mb-1.5 block">CNPJ</Label>
              <Input
                id="cnpj"
                inputMode="numeric"
                placeholder="00.000.000/0000-00"
                value={cnpjValue}
                {...register('cnpj', {
                  required: 'Obrigatório',
                  validate: (v) =>
                    v.replace(/\D/g, '').length === 14 || 'CNPJ deve ter 14 dígitos',
                })}
                onChange={handleCnpjChange}
              />
              {errors.cnpj && (
                <p className="text-[11px] text-warn mt-1">{errors.cnpj.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="razaoSocial" className="mb-1.5 block">Razão social</Label>
              <Input
                id="razaoSocial"
                placeholder="Ex.: Associação Comunitária Nova Esperança"
                {...register('razaoSocial', {
                  required: 'Obrigatório',
                  minLength: { value: 2, message: 'Mínimo 2 caracteres' },
                })}
              />
              {errors.razaoSocial && (
                <p className="text-[11px] text-warn mt-1">{errors.razaoSocial.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="nomeFantasia" className="mb-1.5 block">
                Nome fantasia <span className="text-fg-dim font-normal">(opcional)</span>
              </Label>
              <Input
                id="nomeFantasia"
                placeholder="Ex.: Nova Esperança"
                {...register('nomeFantasia')}
              />
            </div>

            <div>
              <Label htmlFor="enderecoCompleto" className="mb-1.5 block">
                Endereço completo <span className="text-fg-dim font-normal">(opcional)</span>
              </Label>
              <Input
                id="enderecoCompleto"
                placeholder="Ex.: Rua das Flores, 123, Bairro Centro"
                {...register('enderecoCompleto')}
              />
            </div>

            <div>
              <Label htmlFor="municipioUf" className="mb-1.5 block">
                Município/UF <span className="text-fg-dim font-normal">(opcional)</span>
              </Label>
              <Input
                id="municipioUf"
                placeholder="Ex.: Suzano/SP"
                {...register('municipioUf')}
              />
            </div>
          </SheetBody>
          <SheetFooter>
            <SheetClose asChild>
              <Button variant="ghost" type="button">Cancelar</Button>
            </SheetClose>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                  Salvando…
                </>
              ) : (
                'Cadastrar entidade'
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
